// Reactions, edits and deletions are separate encrypted messages that point at a target
// message id. This folds them into per-message state for display.
import type { StoredMessage } from '../storage/db'

export interface MessageState {
  /** Current body (after edits). */
  body: string | null
  edited: boolean
  deleted: boolean
  /** Hidden for everyone by a moderator (the content is still here, for review). */
  hidden: boolean
  /** emoji → users who reacted (in order). */
  reactions: Map<string, string[]>
}

/**
 * `mayHide(actor, author)`: whether a hide from `actor` counts on `author`'s message (the
 * space's moderation rules); without it, hides are ignored.
 */
export function annotations(
  msgs: StoredMessage[],
  mayHide?: (actor: string, author: string) => boolean,
): Map<string, MessageState> {
  const byId = new Map<string, StoredMessage>()
  const state = new Map<string, MessageState>()
  for (const m of msgs) {
    if (m.envelope.type === 'text' || m.envelope.type === 'media' || m.envelope.type === 'library') {
      byId.set(m.envelope.id, m)
      state.set(m.envelope.id, {
        body: 'body' in m.envelope ? (m.envelope.body ?? null) : null,
        edited: false,
        deleted: false,
        hidden: false,
        reactions: new Map(),
      })
    }
  }
  for (const m of msgs) {
    const e = m.envelope
    if (e.type !== 'reaction' && e.type !== 'edit' && e.type !== 'delete' && e.type !== 'hide') continue
    const target = byId.get(e.target)
    const s = state.get(e.target)
    if (!target || !s) continue
    const author = target.sender.user_id
    const actor = m.sender.user_id
    if (e.type === 'hide') {
      if (mayHide?.(actor, author)) s.hidden = e.hidden
    } else if (e.type === 'reaction') {
      const users = s.reactions.get(e.emoji) ?? []
      const has = users.includes(actor)
      if (e.remove && has) users.splice(users.indexOf(actor), 1)
      if (!e.remove && !has) users.push(actor)
      if (users.length) s.reactions.set(e.emoji, users)
      else s.reactions.delete(e.emoji)
    } else if (actor === author && !s.deleted) {
      // Only the author may edit or delete; anything else is ignored (a client could
      // forge such an envelope, but every client applies the same rule).
      if (e.type === 'edit') {
        s.body = e.body
        s.edited = true
      } else {
        s.deleted = true
        s.body = null
        s.reactions.clear()
      }
    }
  }
  return state
}

/** An attachment in words, by its type: "Photo · beach.jpg". */
export function attachmentLabel(media: { name: string; mime: string }): string {
  const kind = media.mime.split('/')[0]
  const word = kind === 'image' ? 'Photo' : kind === 'video' ? 'Video' : kind === 'audio' ? 'Audio' : 'File'
  return `${word} · ${media.name}`
}

/** Short preview of a message for reply quotes, threads and notifications. */
export function preview(m: StoredMessage | undefined, s: MessageState | undefined): string {
  if (!m) return 'a message'
  if (s?.deleted) return 'deleted message'
  if (s?.hidden) return 'hidden message'
  const e = m.envelope
  if (e.type === 'text') return (s?.body ?? e.body).slice(0, 120)
  if (e.type === 'media') return attachmentLabel(e.media)
  if (e.type === 'library') return `Library · ${e.item.title}`
  return 'a message'
}

export const QUICK_REACTIONS = ['👍', '👎', '❤️']

/** The picker's grid; any other emoji can be typed into it. */
export const MORE_REACTIONS = [
  '😂 🤣 😊 😍 🥰 😘 😎 🤩 🥳 😮 😲 🤯 😢 😭 😤 😡 🤔 🤨 🙄 😬 😴 🤤 🤢 🥴 😇 🤗 🫡 🫠 😅 😆',
  '🙏 👏 🙌 💪 👀 👌 ✌️ 🤞 🤝 👋 🫶 🤘 👉 ☝️ 💯 🔥 ✨ 🎉 🎊 💀 💩 🤡 👻 🤖 🎶 🎵 🎧 🎸 🎤 🍿',
  '🧡 💛 💚 💙 💜 🖤 🤍 💔 ❤️‍🔥 💖 ⭐ 🌟 ☀️ 🌈 ⚡ 💥 🎯 🏆 🚀 💡 ✅ ❌ ⚠️ ❓ ❗ ➕ 🍕 🍺 ☕ 🌮',
]
  .join(' ')
  .split(' ')

/** The single emoji in `text` (surrounding space ignored), or null if it's anything else. */
export function singleEmoji(text: string): string | null {
  const t = text.trim()
  const parts = [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(t)]
  if (parts.length !== 1) return null
  return /\p{Extended_Pictographic}|\p{Regional_Indicator}/u.test(t) ? t : null
}
